import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef
} from "react";
import {ANALYTICS_EVENT} from "./constants";

const AnalyticsContext = createContext({
  subscribe: () => {},
  dispatchEvent: () => {}
});

export const useAnalytics = () => useContext(AnalyticsContext);

const AnalyticsWrapper = ({ children }) => {
  return <>{children}</>;
};

export const Analytics = ({ debug = false, children }) => {
  const subscribers = useRef([]);

  const subscribe = fn => {
    subscribers.current.push(fn);
    return () => {
      subscribers.current = subscribers.current.filter(f => f !== fn);
    };
  };

  const dispatchEvent = (eventType, payload) => {
    if (debug) {
      console.info("Analytics Event:", eventType, payload);
    }
    subscribers.current.forEach(fn => fn(eventType, payload));
  };

  return (
    <AnalyticsContext.Provider value={{ subscribe, dispatchEvent }}>
      <AnalyticsWrapper>{children}</AnalyticsWrapper>
    </AnalyticsContext.Provider>
  );
};

export const usePageViewTracking = () => {
    const track = useTracking(ANALYTICS_EVENT.PAGE_VIEW);
    const trackPageView = useCallback((viewName) => {
        track({ name: viewName } );
    },[track]);
    return trackPageView;
};

export const useGlobalAnalyticsContext = () => {
    const trackSet = useTracking(ANALYTICS_EVENT.SET_GLOBAL_PARAM);
    const trackUnset = useTracking(ANALYTICS_EVENT.UNSET_GLOBAL_PARAM);
    const set = useCallback((key, value) => {
        trackSet({ key, value });
    }, [trackSet]);
    const unset = useCallback((key) => {
        trackUnset({ key });
    }, [trackUnset]);
    return [set, unset];
}

export const useTracking = eventType => {
  const { dispatchEvent } = useAnalytics();
  return useCallback(
    (data = {}, onTrack = () => {}) => {
      dispatchEvent(eventType, data);
      onTrack();
    },
    [dispatchEvent, eventType]
  );
};

export const useInviteTracking = (invitePayload) => {
    const track = useTracking(ANALYTICS_EVENT.USER_INFO);
    const [ setGlobalContext ] = useGlobalAnalyticsContext();
    useEffect(() => {
        track({
            userId: invitePayload?.lead?.id,
            userName: [invitePayload?.lead?.firstName, invitePayload?.lead?.lastName].filter(v => !!v).join(" "),
            customer: invitePayload?.customer?.companyName,
            customerId: invitePayload?.customer?.id
        })
        setGlobalContext("inviteType", invitePayload?.type);
        setGlobalContext("language", invitePayload?.lng);
    },
    [invitePayload, track, setGlobalContext]
  );

};

export default Analytics;
